import React, { useState } from 'react';
import { Icon } from "./Icon";
import type { LeadFormData } from '../types/lead';

// Actualizar el tipo LeadFormData para incluir el teléfono
interface ExtendedLeadFormData extends LeadFormData {
    phone: string;
    country_code: string;
}

interface LeadFormCardProps {
    creating: boolean;
    isVisible: boolean;
    onClose: () => void;
    onSubmit: (formData: ExtendedLeadFormData) => Promise<{ success: boolean, message?: string }>;
    initialData?: Partial<ExtendedLeadFormData>;
}

// Lista de países con códigos telefónicos
const countries = [
    { code: 'EC', name: 'Ecuador', dialCode: '+593', flag: '🇪🇨' },
    { code: 'US', name: 'Estados Unidos', dialCode: '+1', flag: '🇺🇸' },
    { code: 'CO', name: 'Colombia', dialCode: '+57', flag: '🇨🇴' },
    { code: 'PE', name: 'Perú', dialCode: '+51', flag: '🇵🇪' },
    { code: 'MX', name: 'México', dialCode: '+52', flag: '🇲🇽' },
    { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
    { code: 'CL', name: 'Chile', dialCode: '+56', flag: '🇨🇱' },
    { code: 'VE', name: 'Venezuela', dialCode: '+58', flag: '🇻🇪' },
    { code: 'ES', name: 'España', dialCode: '+34', flag: '🇪🇸' },
    { code: 'BR', name: 'Brasil', dialCode: '+55', flag: '🇧🇷' },
];

// Componente principal del formulario
export const LeadFormCard: React.FC<LeadFormCardProps> = ({
    creating,
    isVisible,
    onClose,
    onSubmit,
    initialData = {}
}) => {
    const [formData, setFormData] = useState<ExtendedLeadFormData>({
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        country_code: initialData.country_code || 'EC',
        lead_source_id: initialData.lead_source_id || 0,
        lead_process_id: initialData.lead_process_id || 1,
        lead_status_id: initialData.lead_status_id || 1
    });
    const [stateSendForm, setStateSendForm] = useState(false);
    const [notification, setNotification] = useState(false);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;

        let inputValue: string | boolean | number = value;

        if (type === 'checkbox') {
            inputValue = (e.target as HTMLInputElement).checked;
        } else if (type === 'select-one' && name !== 'country_code') {
            inputValue = Number(value);
        } else if (name === 'phone') {
            const numericValue = value.replace(/[^0-9]/g, '');
            setFormData(prev => ({ ...prev, [name]: numericValue }));
        }

        setFormData((prev) => ({
            ...prev,
            [name]: inputValue,
        }));
    };

    const handleSubmit = async () => {
        if (!formData.first_name || !formData.last_name) {
            alert('Por favor, completa todos los campos obligatorios.');
            return;
        }

        // Combinar código de país con número de teléfono
        const selectedCountry = countries.find(c => c.code === formData.country_code);
        const fullPhone = formData.phone ? `${selectedCountry?.dialCode} ${formData.phone}` : '';

        const submitData = {
            ...formData,
            phone: fullPhone
        };

        const response = await onSubmit(submitData);

        if (response.success) {
            setStateSendForm(true);
            removeDataForm();
        } else {
            setStateSendForm(true);
        }
        setNotification(true);
    };

    const removeDataForm = () => {
        setFormData({
            first_name: initialData.first_name || '',
            last_name: initialData.last_name || '',
            email: initialData.email || '',
            phone: initialData.phone || '',
            country_code: initialData.country_code || 'EC',
            lead_source_id: initialData.lead_source_id || 0,
            lead_process_id: initialData.lead_process_id || 1,
            lead_status_id: initialData.lead_status_id || 1
        });
        const timeout = setTimeout(() => {
            setNotification(false);
            onClose();
        }, 1500);
        return () => {
            clearTimeout(timeout);
        };
    }

    if (!isVisible) {
        return null;
    }



    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full mx-auto mb-8">
            <div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-4">
                    {/* Primera fila */}
                    <div>
                        <input
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 placeholder-gray-500 dark:placeholder-gray-400"
                            placeholder="Nombres..."
                            required
                        />
                    </div>
                    <div>
                        <input
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 placeholder-gray-500 dark:placeholder-gray-400"
                            placeholder="Apellidos..."
                            required
                        />
                    </div>

                    {/* Segunda fila */}
                    <div>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 placeholder-gray-500 dark:placeholder-gray-400"
                            placeholder="Email..."
                        />
                    </div>

                    {/* Campo de teléfono con selector de país personalizado */}
                    <div>
                        <div className="flex">
                            {/* Selector de país */}
                            <select
                                name="country_code"
                                value={formData.country_code}
                                onChange={handleInputChange}
                                className="px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 border-r-0"
                            >
                                {countries.map((country) => (
                                    <option key={country.code} value={country.code}>
                                        {country.flag} {country.dialCode}
                                    </option>
                                ))}
                            </select>

                            {/* Campo de número */}
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                name="phone"
                                value={formData.phone}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, ''); // Solo 0-9
                                    handleInputChange({ target: { name: 'phone', value } } as any);
                                }}
                                className="flex-1 px-4 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 placeholder-gray-500 dark:placeholder-gray-400"
                                placeholder="Número de teléfono..."
                            />
                        </div>
                    </div>

                    {/* Tercera fila */}
                    <div className="lg:col-span-1">
                        <select
                            name="lead_source_id"
                            value={formData.lead_source_id}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        >
                            <option value={0}>Seleccionar fuente...</option>
                            <option value={1}>Facebook</option>
                            <option value={2}>Google Forms</option>
                            <option value={3}>Eventos</option>
                            <option value={35}>Whatsapp</option>
                            <option value={36}>Web</option>
                            <option value={37}>Visitas</option>
                            <option value={38}>Tik Tok</option>
                            <option value={39}>Referidos</option>
                        </select>
                    </div>
                </div>

                {/* Botones de acción */}
                <div className="flex justify-center lg:justify-end flex-col sm:flex-row mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {notification && (
                        <div className={`flex items-center mb-2 sm:mb-0 sm:mr-4 px-3 py-2 text-sm rounded-lg border transition-all duration-200 ${stateSendForm
                            ? "text-green-700 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-900/20 dark:border-green-800"
                            : "text-red-700 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/20 dark:border-red-800"
                            }`}>
                            <Icon
                                name={stateSendForm ? "check" : "close"}
                                className="w-4 h-4 mr-2"
                            />
                            <span className="font-medium">
                                {stateSendForm ? "Lead Agregado!" : "Error al crear lead"}
                            </span>
                        </div>
                    )}

                    <button
                        type="button"
                        disabled={creating}
                        onClick={handleSubmit}
                        className={`flex items-center justify-center gap-2 px-6 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${creating
                            ? "bg-blue-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
                            } text-white shadow-lg hover:shadow-xl`}
                    >
                        {creating && (
                            <Icon
                                name="loading"
                                className="w-4 h-4 animate-spin"
                            />
                        )}
                        {creating ? "Creando..." : "Crear Lead"}
                    </button>
                </div>
            </div>
        </div>
    );
};